import {
  add,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  parse
} from 'date-fns';
import get from 'lodash/get';
import { TFunction } from 'react-i18next';
import { ReactComponent as ConfluenceIcon } from 'src/assets/Confluence/confluence.svg';
import { ReactComponent as DatevIcon } from 'src/assets/McpConnectors/datev.svg';
import { ReactComponent as GmailIcon } from 'src/assets/McpConnectors/gmail.svg';
import { ReactComponent as SapIcon } from 'src/assets/McpConnectors/sap.svg';
import { ReactComponent as GoogleCalenderIcon } from 'src/assets/McpConnectors/google-calendar.svg';
import { ReactComponent as GoogleDocsIcon } from 'src/assets/McpConnectors/google-docs.svg';
import { ReactComponent as GoogleDriveIcon } from 'src/assets/McpConnectors/google-drive.svg';
import { ReactComponent as GoogleSheetsIcon } from 'src/assets/McpConnectors/google-sheets.svg';
import { ReactComponent as OneDriveIcon } from 'src/assets/onedrive.svg';
import { ReactComponent as SharepointIcon } from 'src/assets/sharepoint.svg';
import {
  ATTACH_FILE_ICON_HEIGHT,
  ATTACH_FILE_ICON_WIDTH,
  ChatAttachedFileProps,
  UPLOADED_IMAGE_ICON_HEIGHT,
  UPLOADED_IMAGE_ICON_WIDTH
} from 'src/content/Chat/utils';
import { areFilesValid } from 'src/content/Documents/UploadDocument/utils';
import { normalizeThinkingEfforts } from 'src/utils/thinking-effort';
import { setAlert } from 'src/redux/slices/snackbar';
import { AppDispatch } from 'src/redux/store';
import { IResourceProvider } from 'src/types/api';
import { IAiModel, StringKeys } from 'src/types/base';
import { formatFileSize } from 'src/utils/numberFormat';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import {
  ACTIONS_CATEGORIES,
  ALERT,
  DEFAULT_LANG,
  FILE_UPLOADING_STATUS,
  LOOKUP,
  RESOURCE_PROVIDERS,
  SUPPORTED_IMAGE_FORMATS,
  SUPPORTED_UPLOAD_FILES_FORMAT,
  TOOLS_LIST
} from '../types/enum';
import {
  EMAIL_REG_EXP,
  MAX_FILENAME_DISPLAY_LENGTH,
  PUBLIC_ROUTES,
  SPECIAL_CHAR_PATTERN_REGEX,
  TWENTY_FOUR_HOUR_FORMAT,
  UPLOADED_FILE_IDS,
  UploadFile
} from './constants';
import { decrypt_local, encrypt_local } from './encryption';

import { ReactComponent as MicrosoftOnedriveIcon } from 'src/assets/McpConnectors/onedrive.svg';
import { ReactComponent as OutlookCalendarIcon } from 'src/assets/McpConnectors/outlook-calendar.svg';
import { ReactComponent as OutlookEmailIcon } from 'src/assets/McpConnectors/outlook-email.svg';
import { ReactComponent as MicrosoftSharepointIcon } from 'src/assets/McpConnectors/sharepoint.svg';
import { ReactComponent as SlackIcon } from 'src/assets/McpConnectors/slack.svg';

import { ReactComponent as MicrosoftTeamsIcon } from 'src/assets/McpConnectors/team-icon.svg';

import clsx, { type ClassValue } from 'clsx';
import { ReactComponent as HubspotIcon } from 'src/assets/McpConnectors/hubspot.svg';
import { ReactComponent as JiraIcon } from 'src/assets/McpConnectors/jira.svg';
import { ReactComponent as ConfluenceMcpIcon } from 'src/assets/McpConnectors/confluence-mcp.svg';
import { ReactComponent as SqlDatabaseIcon } from 'src/assets/McpConnectors/sql-database.svg';
import { ReactComponent as MicrosoftD365Icon } from 'src/assets/McpConnectors/microsoft-d365.svg';
import { languages } from 'src/content/Settings/Profile/languages';
import {
  kcinit,
  removeRealmDomain,
  setClientId,
  setKcLang,
  setRealmDomain,
  setRealmName
} from 'src/keycloak';
import { extendTailwindMerge } from 'tailwind-merge';
import { BASE_SERVER_URL, ENV_BASE_URL, POST_DOMAIN_PART } from 'src/config';
import axios from 'src/utils/axios';
import logger from './logger';
import { renderApp } from 'src';

const FILE_SIZE_LIMITS_MB = 10;

const AZURE_PORTAL_URL = 'https://portal.azure.com/#home';

const ATLASSIAN_ADMIN_PORTAL_URL =
  'https://id.atlassian.com/manage-profile/security/api-tokens';

const ATLASSIAN_DEVELOPER_PORTAL_URL =
  'https://id.atlassian.com/login?continue=https://developer.atlassian.com/';

export const PRIVACY_POLICY_URL_EN = 'https://empowergpt.ai/en/privacy-policy/';
export const PRIVACY_POLICY_URL_DE = 'https://empowergpt.ai/datenschutz/';
export const PRIVACY_POLICY_URL_IT = 'https://empowergpt.ai/en/privacy-policy/';

export const SCROLL_TOLERANCE = 1;

export const INVALID_USER_STATUS = 'INVALID_USER';
export const SESSION_EXPIRED_STATUS = 'SESSION_EXPIRED';
export const INVALID_TOKEN_STATUS = 'INVALID_TOKEN';
export const RETRYABLE_HTTP_STATUS_CODES = [429, 502, 503];
export const MAX_REQUEST_RETRIES = 3;
export const INVALID_USER_RETRY_DELAY = 2000; // miliseconds
export const PAGE_TITLE_WEIGHT = 700;
export const PAGE_TITLE_SIZE = '25px';

export const SYSADMIN_REALM = 'empowergpt_com';
export const WF_ALLOWED_TENANTS = [
  'R_TEN_ALPINE',
  'R_TEN_INTECWW_COM',
  'R_TEN_INTECH_COM'
] as const;
export type WorkflowTenant = (typeof WF_ALLOWED_TENANTS)[number];

export const SILENCE_TIMEOUT_MS = 6000;
export const CONSUMPTION_TYPE = 'Chat';

export const getInitials = (name: string) => {
  // Sanitize the string:
  if (name) {
    const sanitizedStr = name
      ?.trim() // Remove leading/trailing spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/[^a-zA-Z0-9' ]/g, ' ') // Replace non-alphanumeric characters with space and allowing apostrophy
      .toUpperCase() // Convert to uppercase for consistency
      .split(' ') // Split the string on space
      .slice(0, 2); // Select only first 2 words

    // Get the initials:
    if (sanitizedStr.length === 1) {
      return sanitizedStr[0].charAt(0) + sanitizedStr[0].charAt(1);
    }
    return sanitizedStr.map((word) => word.charAt(0)).join('');
  } else return '';
};

export const formatDate = (date?: string | Date, date_format?: string) => {
  return date
    ? format(new Date(`${date}`), date_format || TWENTY_FOUR_HOUR_FORMAT)
    : '';
};

export const caseInsensitiveSort = (a: string, b: string) => {
  return a.toLowerCase().localeCompare(b.toLowerCase());
};

export const viewPDFDocument = (data, pageNo?: number) => {
  const url = window.URL.createObjectURL(data);
  window.open(`${url}${pageNo ? `#page=${pageNo}` : ''}`);
};

export const convertDate = (date: Date, t: any) => {
  let currentDate: any = new Date();
  let previousDate: any = new Date(date);

  const days = differenceInDays(currentDate, previousDate);
  const hours = differenceInHours(currentDate, previousDate);
  const mins = differenceInMinutes(currentDate, previousDate);
  const secs = differenceInSeconds(currentDate, previousDate);

  if (days > 0) {
    return `${days} ${t(days > 1 ? T.days : T.day)}`;
  } else if (hours > 0) {
    return `${hours} ${t(hours > 1 ? T.hours : T.hour)}`;
  } else if (mins > 0) {
    return `${mins} ${t(mins > 1 ? T.mins : T.min)}`;
  } else if (secs > 0) {
    return `${secs} ${t(secs > 1 ? T.secs : T.sec)}`;
  }
  return '';
};

export const getFileType = (
  name: string,
  source: string,
  format: string,
  original_format: string
) => {
  if (original_format) return original_format;
  else if (name) return name?.split('.')?.pop();
  else if (format) return format;
  else if (source) return source?.split('.')?.pop();

  return '';
};

export const isEmailValid = (value: string) => {
  return !!value?.match(EMAIL_REG_EXP) || false;
};

// TODO: separate files and folders related utilities
export const getProviderForCurrentResource = (
  providerId: string,
  resourceProviders: IResourceProvider[]
): RESOURCE_PROVIDERS | undefined => {
  return resourceProviders.find(({ id }) => id === +providerId)?.type;
};

export const autoScrollIntoView = (
  errors: string[],
  block: ScrollLogicalPosition = 'center',
  getBy: 'id' | 'name' = 'id'
) => {
  const firstErrorField = errors?.[0] ?? '';
  if (firstErrorField) {
    const field =
      getBy === 'id'
        ? document.getElementById(firstErrorField)
        : document.getElementsByName(firstErrorField)[0];
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block });
    }
  }
};

export const getOptionLabelValue = (
  option: StringKeys,
  optionLabel: string | string[]
) => {
  if (typeof option === 'string') return option;
  const keys = Array.isArray(optionLabel) ? optionLabel : [optionLabel];
  for (const key of keys) {
    const label = get(option, key);
    if (label && String(label).trim() !== '') return label;
  }
  return undefined;
};

export const filterEmptyValuesFromList = (
  options: StringKeys,
  optionLabel: string | string[]
) =>
  (options?.length ? options : options?.data)?.filter((option) => {
    const label = getOptionLabelValue(option, optionLabel);
    return label && String(label).trim() !== '';
  });

export const getLanguageName = (language: string, code: string) => {
  if (code) {
    try {
      return new Intl.DisplayNames([language], { type: 'language' }).of(code);
    } catch (err) {
      return '';
    }
  }
  return '';
};

export const openAzurePortal = async () => {
  window.open(AZURE_PORTAL_URL, '_blank');
};

export const isValidURL = (url: string) => {
  const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;
  return regex.test(url);
};

export const isPublicRoute = (): boolean => {
  if (PUBLIC_ROUTES.includes(window.location.pathname)) {
    return true;
  }
  return false;
};

export const isDefaultDomain = () => {
  const host = window.location.hostname.toLowerCase();
  return host === POST_DOMAIN_PART;
};

export const getBrowserLanguage = (): string => {
  return navigator?.language?.split('-')?.[0];
};
// This function is removed from Conversation which is being used for Conversation box height .Place here to ensure that it may be get helpful in future
// const getConversationBoxHeight = () => {
//   const isDocumentViewerOpen = chatContexts[chatId]?.isDocumentViewerOpen;

//   return {
//     [theme.breakpoints.up('xl')]: {
//       height: isDocumentViewerOpen ? `calc(100% - 190px)` : baseHeight
//     },
//     [theme.breakpoints.down('xl')]: {
//       height: isDocumentViewerOpen ? `calc(100% - 200px)` : baseHeight
//     }
//   };
// };

export const isSupportedFileType = (file: File) =>
  [
    ...(Object.values(SUPPORTED_IMAGE_FORMATS) as string[]),
    ...(Object.values(SUPPORTED_UPLOAD_FILES_FORMAT) as string[])
  ].includes(file.type);

export const validateFiles = (
  selectedFiles: ChatAttachedFileProps[],
  uploadedFiles: ChatAttachedFileProps[],
  transactionId: string,
  dispatch: AppDispatch,
  t: TFunction<'translation', undefined>,
  fileSizeLimit: number
) => {
  return selectedFiles
    .map((file) => {
      let maxSizeMB = fileSizeLimit;
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const transactionPath = `${transactionId}/${file.name}`;
      const isFileSupported = isSupportedFileType(file);
      for (const [typePrefix, sizeLimit] of Object.entries(
        FILE_SIZE_LIMITS_MB
      )) {
        if (file.type === typePrefix) {
          maxSizeMB = sizeLimit;
          break;
        }
      }
      const isFileSizeValid = file.size <= maxSizeMB * 1024 * 1024;
      const isFileNameLengthValid = areFilesValid(Array.of(file), dispatch, t);
      const isDuplicateFile = uploadedFiles.some(
        (uploadedFile) => uploadedFile.name === file.name
      );

      if (!isFileSizeValid) {
        setTimeout(
          () =>
            dispatch(
              setAlert({
                msg: t(T.maxChatFileSize, {
                  name: file.name.slice(0, 15) + '...',
                  size: maxSizeMB,
                  type: fileExtension.toUpperCase()
                }),
                type: ALERT.ERROR
              })
            ),
          200
        );
      }
      if (!isFileSupported) {
        setTimeout(
          () =>
            dispatch(
              setAlert({
                msg: t(T.unSupportedFile, {
                  name: file.name.slice(0, 15) + '...'
                }),
                type: ALERT.ERROR
              })
            ),
          200
        );
      }
      if (
        isFileSupported &&
        isFileSizeValid &&
        isFileNameLengthValid &&
        !isDuplicateFile
      ) {
        const fileWithProps = file as File & {
          path: string;
          fileType: string;
          status_code: string;
        };
        fileWithProps.path = transactionPath;
        fileWithProps.fileType = fileExtension;
        fileWithProps.status_code = FILE_UPLOADING_STATUS.UPLOADING;
        return fileWithProps;
      }
    })
    .filter(Boolean) as (File & { path: string; fileType: string })[];
};

export const getBase64Src = (fileData: string) => {
  let mimeType: string;

  switch (true) {
    case fileData.startsWith('/9j'):
      mimeType = 'image/jpeg';
      break;
    case fileData.startsWith('iVBORw0KGgo'):
      mimeType = 'image/png';
      break;
    case fileData.startsWith('R0lGODdh'):
    case fileData.startsWith('R0lGODlh'):
      mimeType = 'image/gif';
      break;
    default:
      mimeType = 'image/jpeg';
  }

  return `data:${mimeType};base64,${fileData}`;
};

export const openAtlassianPortal = async () => {
  window.open(ATLASSIAN_ADMIN_PORTAL_URL, '_blank');
};

export const SUPPORT_EMAIL_EGPT = 'support@empowergpt.ai';

export const openAtlassianAppDeveloperPortal = async () => {
  window.open(ATLASSIAN_DEVELOPER_PORTAL_URL, '_blank');
};

export const getResourceProviderIcon = (type: RESOURCE_PROVIDERS) => {
  switch (type) {
    case RESOURCE_PROVIDERS.SHAREPOINT:
      return SharepointIcon;
    case RESOURCE_PROVIDERS.ONEDRIVE:
      return OneDriveIcon;
    case RESOURCE_PROVIDERS.CONFLUENCE:
      return ConfluenceIcon;
    default:
      return null;
  }
};
export const getModelData = (option): IAiModel => {
  return {
    id: option.id,
    name: option.name,
    displayName: option.display_name,
    description: option.description,
    comment: option.comment,
    commentDetails: option.comment_details,
    accuracy: option.accuracy,
    price: option.price,
    speed: option.speed,
    priority: option.priority,
    cutoffDate: option.cutoff_date,
    family: option.family,
    deploymentProvider: option.deployment_provider,
    imageAnalysis: option.image_analysis,
    isEnabled: option.is_enabled,
    region: option.region,
    tokenLimit: option.token_limit,
    contextWindow: option.context_window ?? null,
    isDefault: option.is_default,
    isCanvasModel: option.is_chat_canvas_model,
    isMiniExpertModel: option.is_mini_expert_model,
    thinkingEfforts: normalizeThinkingEfforts(
      option.thinking_efforts ?? option.thinkingEfforts
    )
  };
};

export const isAnImage = (file) =>
  Object.values(SUPPORTED_IMAGE_FORMATS).includes(
    file?.type || `image/${file?.original_format}`
  );

export const sanitizedFiles = (uploadedFiles, currentFilesUploadingCount) =>
  uploadedFiles
    .slice(-currentFilesUploadingCount)
    .map(({ uploadedFile, ...rest }) => rest);

export const getChatImgStyles = (showStatus: boolean) => ({
  borderRadius: showStatus ? '4px' : '8px',
  maxHeight: UPLOADED_IMAGE_ICON_HEIGHT,
  maxWidth: UPLOADED_IMAGE_ICON_WIDTH,
  height: showStatus ? ATTACH_FILE_ICON_HEIGHT : 'auto',
  width: showStatus ? ATTACH_FILE_ICON_WIDTH : 'auto'
});

export const getReactionCode = (lookups, reactionId: number) => {
  return lookups[LOOKUP.REACTION]?.find((item) => item.id === reactionId)?.code;
};

export const getReactionsKeyValue = (lookups) => {
  let reactions: { [code: string]: number } = {};
  lookups[LOOKUP.REACTION]?.forEach((item) => {
    reactions[item.code] = item.id;
  });
  return reactions;
};

export const BLOCKED_DOMAINS =
  import.meta.env.REACT_APP_TENANT_BLOCKED_DOMAINS ||
  'gmail.com,yahoo.com,outlook.com,hotmail.com,aol.com,icloud.com,mail.com,zoho.com';
export const getReactionCodeById = (lookups, id: number): string | null => {
  const reactions = getReactionsKeyValue(lookups);
  return Object.keys(reactions).find((key) => reactions[key] === id) || null;
};

export const getUploadedFileIds = () => {
  return window.localStorage.getItem(UPLOADED_FILE_IDS)
    ? decrypt_local(window.localStorage.getItem(UPLOADED_FILE_IDS))
    : null;
};
export const setUploadedFileIds = (fileIds: string) =>
  window.localStorage.setItem(UPLOADED_FILE_IDS, encrypt_local(fileIds));

export const removeUploadingFilesId = (fileId?: number) => {
  let files = getUploadedFileIds() ? JSON.parse(getUploadedFileIds()) : [];
  if (fileId) {
    files = files.filter((ids) => ids != fileId);
  } else {
    files = [];
  }

  setUploadedFileIds(JSON.stringify(files));
};

export const addFileToUploadingFileIds = (fileIds: number[]) => {
  let files = getUploadedFileIds() ? JSON.parse(getUploadedFileIds()) : [];
  fileIds.forEach((fileId) => {
    files.push(fileId);
  });
  setUploadedFileIds(JSON.stringify(files));
};

export function getFormattedAvailableTime(restrictedTime: string): {
  availableAt: string;
  availableTime: Date;
} {
  const normalizedTime = restrictedTime.split('.')[0];

  const duration = parse(normalizedTime, 'HH:mm:ss', new Date());

  const availableTime = add(new Date(), {
    hours: duration.getHours(),
    minutes: duration.getMinutes(),
    seconds: duration.getSeconds()
  });

  const availableAt = format(availableTime, 'hh:mm a');
  return { availableAt, availableTime };
}

function isValidEmail(email: string): boolean {
  return EMAIL_REG_EXP.test(email);
}

export const isWorkEmail = (email: string) => {
  const domain = email.split('@')[1];
  if (!domain) return false;
  return !BLOCKED_DOMAINS.split(',').includes(domain);
};

// Strips spaces and lowercases — apply on every keystroke
export const sanitizeEmailInput = (value: string): string =>
  value.replace(/\s/g, '').toLowerCase();

// Returns a translation key if invalid, null if valid
export const validateEmailField = (email: string): string | null => {
  if (SPECIAL_CHAR_PATTERN_REGEX.test(email)) return T.specialCharNotAllowed;
  if (!EMAIL_REG_EXP.test(email)) return T.invalidEmail;
  if (!isWorkEmail(email)) return T.workEmailRequired;
  return null;
};

export const formatDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const getFileStatusCode = (
  lookups: {
    [lookup_type: string]: any[];
  },
  statusId: number
) => {
  return lookups?.[LOOKUP.FILES]?.find((item) => item.id === statusId)?.code;
};

export const MCP_ACTIONS_LOGO_MAP: Record<
  string,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  [ACTIONS_CATEGORIES.OUTLOOK_EMAIL]: OutlookEmailIcon,
  [ACTIONS_CATEGORIES.OUTLOOK_CALENDAR]: OutlookCalendarIcon,
  [ACTIONS_CATEGORIES.GMAIL]: GmailIcon,
  [ACTIONS_CATEGORIES.GOOGLE_CALENDAR]: GoogleCalenderIcon,
  [ACTIONS_CATEGORIES.SLACK]: SlackIcon,
  [ACTIONS_CATEGORIES.GOOGLE_DRIVE]: GoogleDriveIcon,
  [ACTIONS_CATEGORIES.HUBSPOT]: HubspotIcon,
  [ACTIONS_CATEGORIES.JIRA]: JiraIcon,
  [ACTIONS_CATEGORIES.CONFLUENCE]: ConfluenceMcpIcon,
  [ACTIONS_CATEGORIES.GOOGLE_SHEETS]: GoogleSheetsIcon,
  [ACTIONS_CATEGORIES.MICROSOFT_ONEDRIVE]: MicrosoftOnedriveIcon,
  [ACTIONS_CATEGORIES.MICROSOFT_SHAREPOINT]: MicrosoftSharepointIcon,

  [ACTIONS_CATEGORIES.MICROSOFT_TEAMS]: MicrosoftTeamsIcon,
  [ACTIONS_CATEGORIES.GOOGLE_DOCS]: GoogleDocsIcon,
  [ACTIONS_CATEGORIES.DATEV_ACCOUNTING]: DatevIcon,
  [ACTIONS_CATEGORIES.DATEV_HR]: DatevIcon,
  [ACTIONS_CATEGORIES.SQL_DATABASE]: SqlDatabaseIcon,
  [ACTIONS_CATEGORIES.MICROSOFT_D365]: MicrosoftD365Icon,
  [ACTIONS_CATEGORIES.SAP_S4HANA]: SapIcon
};

export function getEnumKeyByValue<T extends Record<string, string | number>>(
  enumObj: T,
  value: string | number
): keyof T | undefined {
  return (Object.keys(enumObj) as Array<keyof T>).find(
    (key) => enumObj[key] === value
  );
}

export const getBrowsersLanguage = (): string => {
  const browserLang = navigator.language;
  const exactMatch = languages.find((lang) =>
    lang.otherCodes?.includes(browserLang)
  );
  if (exactMatch) return exactMatch.code;
  const baseLang = browserLang.split('-')[0];
  const baseMatch = languages.find((lang) => lang.code === baseLang);
  if (baseMatch) return baseMatch.code;
  return DEFAULT_LANG.EN;
};

export const saveLangFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const uiLocale = urlParams.get('ui_locales');
  if (uiLocale) {
    setKcLang(uiLocale);
  }
};

export const COUNTRY_LANG_MAP: Record<string, 'en' | 'de' | 'it'> = {
  germany: 'de',
  austria: 'de',
  switzerland: 'de',
  italy: 'it',
  'united kingdom': 'en',
  'united states': 'en'
};

// Custom tailwind-merge configuration to support custom spacing and color tokens
// Core spacing scale (used by gap/gap-x/gap-y, which map to --spacing-*)
const CORE_SPACING_SCALE = [
  'none',
  '3xs',
  '2xs',
  'xs',
  's',
  's-m',
  'm',
  'l',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
  '8xl',
  '9xl',
  '10xl',
  '11xl'
];

// Padding/margin scale (--spacing-p-*/--spacing-m-*, only goes up to 7xl)
const PADDING_MARGIN_SCALE = [
  'none',
  '3xs',
  '2xs',
  'xs',
  's',
  's-m',
  'm',
  'l',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl'
];

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // ============================================
      // SPACING TOKENS
      // ============================================
      'font-size': [
        {
          text: [
            '4xl',
            '3xl',
            '2xl',
            'xl',
            'lg',
            'md',
            'bw-s-m',
            'sm',
            'xsm',
            'xxsm'
          ]
        }
      ],
      rounded: [
        {
          rounded: ['bw-2xl-3xl']
        }
      ],
      gap: [{ gap: CORE_SPACING_SCALE }],
      'gap-x': [{ 'gap-x': CORE_SPACING_SCALE }],
      'gap-y': [{ 'gap-y': CORE_SPACING_SCALE }],
      p: [{ p: PADDING_MARGIN_SCALE }],
      px: [{ px: PADDING_MARGIN_SCALE }],
      py: [{ py: PADDING_MARGIN_SCALE }],
      pt: [{ pt: PADDING_MARGIN_SCALE }],
      pr: [{ pr: PADDING_MARGIN_SCALE }],
      pb: [{ pb: PADDING_MARGIN_SCALE }],
      pl: [{ pl: PADDING_MARGIN_SCALE }],
      m: [{ m: PADDING_MARGIN_SCALE }],
      mx: [{ mx: PADDING_MARGIN_SCALE }],
      my: [{ my: PADDING_MARGIN_SCALE }],
      mt: [{ mt: PADDING_MARGIN_SCALE }],
      mr: [{ mr: PADDING_MARGIN_SCALE }],
      mb: [{ mb: PADDING_MARGIN_SCALE }],
      ml: [{ ml: PADDING_MARGIN_SCALE }],
      w: [
        {
          w: [
            'xs',
            's',
            'm',
            'l',
            'xl',
            '2xl',
            '2-5xl',
            '3xl',
            '4xl',
            '5xl',
            '6xl'
          ]
        }
      ],
      h: [
        {
          h: [
            'xs',
            's',
            'm',
            'l',
            'xl',
            '2xl',
            '2-5xl',
            '3xl',
            '4xl',
            '5xl',
            '6xl'
          ]
        }
      ],

      // ============================================
      // COLOR TOKENS
      // ============================================

      // Background colors (bg-*)
      'bg-color': [
        {
          bg: [
            // Base backgrounds
            'main',
            'primary',
            'primary-hover',
            'primary-pressed',
            'secondary',
            'secondary-hover',
            'secondary-pressed',
            'tertiary',
            'tertiary-hover',
            'tertiary-pressed',

            // Brand backgrounds
            'brand',
            'brand-hover',
            'brand-pressed',
            'brand-light',
            'brand-md',
            'brand-md-hover',
            'brand-md-pressed',

            // Secondary brand backgrounds
            'sec-brand-light',
            'sec-brand-md',
            'sec-brand-md-hover',
            'sec-brand-md-pressed',

            // State backgrounds
            'disabled',
            'brand-disabled',

            // Status backgrounds
            'info',
            'info-hover',
            'info-dark',
            'info-dark-hover',
            'info-dark-pressed',
            'success',
            'success-hover',
            'success-dark',
            'success-dark-hover',
            'success-dark-pressed',
            'warning',
            'warning-hover',
            'warning-dark',
            'warning-dark-hover',
            'warning-dark-pressed',
            'error',
            'error-hover',
            'error-dark',
            'error-dark-hover',
            'error-dark-pressed',

            // Button backgrounds (used as bg-btn-*)
            'btn-primary-bg',
            'btn-primary-bg-hover',
            'btn-primary-bg-pressed',
            'btn-secondary-bg',
            'btn-secondary-bg-hover',
            'btn-secondary-bg-pressed',
            'btn-tertiary-bg-hover',
            'btn-tertiary-bg-pressed',
            'btn-brand',
            'btn-brand-hover',
            'btn-brand-pressed',
            'btn-bg-disabled',
            'btn-destructive-bg',
            'btn-destructive-bg-hover',
            'btn-destructive-bg-pressed',
            'btn-destructive-bg-dark',
            'btn-destructive-bg-dark-hover',
            'btn-destructive-bg-dark-pressed',

            // Logo backgrounds (used as bg-logo-*)
            'logo-white',
            'logo-over-dark'
          ]
        }
      ],

      // Text colors (text-*)
      'text-color': [
        {
          text: [
            // Text colors
            'text-primary',
            'text-secondary',
            'text-disabled',
            'text-brand',
            'text-over-brand',
            'text-link',
            'text-link-brand',
            'text-link-brand-hover',

            // Status text colors
            'text-success',
            'text-warning',
            'text-error',
            'text-error-hover',
            'text-info',

            // Icon colors (used with text utility for SVG currentColor)
            'icon-primary',
            'icon-primary-hover',
            'icon-secondary',
            'icon-secondary-hover',
            'icon-brand',
            'icon-brand-hover',
            'icon-over-brand',
            'icon-white',
            'icon-disabled',
            'icon-bg',
            'icon-bg-brand',
            'icon-bg-white',
            'icon-info',
            'icon-error',
            'icon-success',
            'icon-warning',

            // Logo colors
            'logo-brand',
            'logo-white',
            'logo-black',
            'logo-over-dark',

            // Also support direct color names for text
            'brand',
            'link',
            'disabled',
            'error',
            'success',
            'warning',
            'info',

            // Button text colors (used as text-btn-*)
            'btn-primary-fg',
            'btn-primary-fg-hover',
            'btn-primary-fg-pressed',
            'btn-secondary-fg',
            'btn-secondary-fg-hover',
            'btn-secondary-fg-pressed',
            'btn-tertiary-fg',
            'btn-tertiary-fg-hover',
            'btn-tertiary-fg-pressed',
            'btn-fg-disabled',
            'btn-destructive-fg',
            'btn-destructive-fg-hover',
            'btn-destructive-fg-pressed',
            'btn-link-fg',
            'btn-link-fg-hover',
            'btn-link-fg-pressed',
            'btn-link-gray-fg',
            'btn-link-gray-fg-hover',
            'btn-link-gray-fg-pressed',
            'btn-link-gray-light-fg',
            'btn-link-gray-light-fg-hover',
            'btn-link-gray-light-fg-pressed',
            'btn-link-destructive-fg',
            'btn-link-destructive-fg-hover',
            'btn-link-destructive-fg-pressed',

            // Logo colors (used as text-logo-*)
            'logo-over-dark',

            // Others colors (used as text-others-*)
            'others-purple-600'
          ]
        }
      ],

      // Border colors (border-*)
      'border-color': [
        {
          border: [
            // Border colors (note: these are used as border-border-* in your code)
            'border-primary',
            'border-primary-hover',
            'border-secondary',
            'border-secondary-hover',
            'border-brand',
            'border-brand-hover',
            'border-brand-primary',
            'border-brand-secondary',
            'border-gray-dark',
            'border-warning',
            'border-disabled',
            'border-disabled-subtle',
            'border-error',
            'border-error-hover',
            'border-error-subtle',
            'border-info',
            'border-info-hover',
            'border-success',

            // Button border colors (used as border-btn-*)
            'btn-secondary-border',
            'btn-secondary-border-hover',
            'btn-secondary-border-pressed',
            'btn-border-disabled',
            'btn-destructive-border',
            'btn-destructive-border-hover',
            'btn-destructive-border-pressed'
          ]
        }
      ],

      // Outline colors (outline-*) - used instead of border-* so the ring
      // doesn't add to the box size (buttons use outline + -outline-offset-1)
      'outline-color': [
        {
          outline: [
            'border-primary',
            'border-secondary',
            'btn-secondary-border',
            'btn-secondary-border-hover',
            'btn-secondary-border-pressed',
            'btn-border-disabled',
            'btn-destructive-border',
            'btn-destructive-border-hover',
            'btn-destructive-border-pressed'
          ]
        }
      ]
    }
  }
});

export const cn = (...inputs: ClassValue[]) => {
  return customTwMerge(clsx(inputs));
};

export const isImageGenMessage = (toolName: string): boolean => {
  return [TOOLS_LIST.NANO_BANANA].includes(toolName as TOOLS_LIST);
};

export function formatBytes(bytes: number) {
  return formatFileSize(bytes);
}

export function truncateFileName(
  name: string,
  maxLength = MAX_FILENAME_DISPLAY_LENGTH
): string {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
}

export function getFileExtension(file: UploadFile): string {
  return file.name.split('.').pop()?.toLowerCase() ?? '';
}

export function reloadPageOnWpsDisconnect(
  dispatch: AppDispatch,
  t: TFunction<'translation', undefined>
) {
  dispatch(
    setAlert({
      msg: t(T.adTokenExpired, { seconds: '5' }),
      type: ALERT.WARNING
    })
  );
  setTimeout(() => {
    window.location.reload();
  }, 5000);
}

type FetchTenantInfoParams = {
  email?: string;
  language?: string;
};

type TenantInfo = {
  realm: string;
  clientId: string;
  tenantDomain: string;
};

export const fetchTenantInfo = (
  params?: FetchTenantInfoParams
): Promise<TenantInfo | null> => {
  const url = `${BASE_SERVER_URL}/api/v1/tenant/discover`;
  const query = params?.email
    ? `?email=${params.email}&language=${
        params.language ?? getBrowserLanguage()
      }`
    : '';
  return axios
    .get(`${url}${query}`)
    .then((response: any) => {
      if (response.status === 200 && response?.data?.ret_val?.data) {
        return {
          realm: response.data.ret_val.data.realm_name?.trim() ?? null,
          clientId: response.data.ret_val.data.client_id?.trim() ?? null,
          tenantDomain: response.data.ret_val.data.tenant_domain?.trim() ?? null
        };
      }
      return null;
    })
    .catch((error: any) => {
      logger.error(error);
      return null;
    });
};

export const applyTenantInfo = (tenant: TenantInfo) => {
  setRealmName(tenant.realm);
  if (tenant.tenantDomain) {
    setRealmDomain(tenant.tenantDomain);
  } else {
    removeRealmDomain();
  }
  setClientId(tenant.clientId);
};

export const discoverTenantAndInitialize = (divId: string) => {
  fetchTenantInfo().then((tenant) => {
    if (!tenant?.realm || !tenant?.clientId) {
      alert('Tenant not found');
      window.location.href = ENV_BASE_URL;
    }
    applyTenantInfo(tenant);
    kcinit({
      realm: tenant.realm,
      clientId: tenant.clientId,
      userEmail: null
    }).then(() => {
      renderApp(divId);
    });
  });
};
export const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Turns a machine key into a readable label - `abc_def` -> `Abc Def`.
 */
export const humanizeKey = (value: string): string =>
  value
    .replace(/[_\-.:/]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean)
    .map(capitalize)
    .join(' ');

export const isConnectorActive = (
  is_connected?: boolean,
  refresh_expires_at?: string
): boolean => {
  if (!is_connected) return false;
  if (!refresh_expires_at) return true;
  return new Date(refresh_expires_at) > new Date();
};

export const formatRefreshExpiry = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export function toPrimitiveString(value: unknown, fallback: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

export const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
};
